import { FunctionComponent, useEffect, useRef, useState, useCallback } from 'react';

// Libs
import classNames from 'classnames';
import { useRouter } from 'next/router';
import { stringify } from 'query-string';

// context
import { useGetShowIconQuestion } from '@Pages/hoi-dap/Context';

// @Components
import SearchLayout from '@Components/Layout/Search';
import DownloadApp from '@Pages/bai-viet/Components/DownloadApp';
import LayoutContainer from '@Components/Layout/Components/Container';
import Breadcrumb, { PathLevelProps } from '@Components/UI/Breadcrumb';
import InputSearch from '@Components/Layout/Search/Components/InputSearch';
import { useViewDetected, ViewMedia } from '@Components/Utils/ViewDetected';
import LayoutResult from '@Components/Layout/Search/Components/LayoutResult';

// Components
import FormQuestion from '../../Asking/FormQuestion';
import ListFilter from '@Pages/hoi-dap/Components/UI/ListFilter';
import PopularCard, { getQueryPopular } from '@Pages/hoi-dap/Components/UI/PopularCard';

// GA
import { trackingEvent } from '@Components/Utils/Lazy/Script';
import { PageView } from '@Components/Utils/Lazy/Script';

// CSS
import layout from '../Layout/layout.module.scss';

const paths: Array<PathLevelProps> = [
    {
        label: 'Cộng đồng',
        path: '/hoi-dap',
    },
];

const pathDetails: Array<PathLevelProps> = [
    {
        label: 'Hỏi đáp',
        path: '/hoi-dap',
    },
    {
        label: 'Chi tiết câu hỏi',
    },
];

enum typeActionPage {
    lists,
    detail,
}
interface LayoutProps {
    tags?: any;
    listsTags?: any;
    children: React.ReactNode;
    actionPage?: keyof typeof typeActionPage;
}
const Layout: FunctionComponent<LayoutProps> = ({ children, actionPage = 'lists', listsTags, tags }) => {
    const { push, query } = useRouter();
    const { deviceType } = useViewDetected();
    const isMobile = deviceType !== 'desktop';

    // useRef
    const divRef = useRef<HTMLDivElement>();

    const { showQuestionIcon, toggleQuestionIcon, formQuestionHeight } = useGetShowIconQuestion();

    const scrollHandler = useCallback(() => {
        const breadcrumbHeight = divRef?.current?.clientHeight || 0;

        if (window.scrollY >= formQuestionHeight + breadcrumbHeight) {
            toggleQuestionIcon(true);
        } else {
            toggleQuestionIcon(false);
        }
    }, [showQuestionIcon, formQuestionHeight, actionPage]);

    useEffect(() => {
        document.addEventListener('scroll', scrollHandler);

        return () => document.removeEventListener('scroll', scrollHandler);
    }, []);

    const [openFilter, setOpenFilter] = useState<boolean>(false);

    const keyword = (query?.s || '').toString();

    const handleSearch = (keyword: string) => {
        const popularQuery = getQueryPopular(query?.['pho_bien']);
        const queryParams = stringify({ ...query, s: keyword, pho_bien: popularQuery }, { skipEmptyString: true });
        const _push = queryParams?.length ? `/hoi-dap?${queryParams}` : '/hoi-dap';

        // event tracking
        trackingEvent('nhấp vào nút "Tìm kiếm"', 'Tìm kiếm', keyword);

        return push(_push);
    };

    const toggleFilter = () => {
        setOpenFilter(!openFilter);
    };

    const BannerDetail = () => {
        return (
            <>
                <FormQuestion active className={layout.form_question} formClassName={layout.form} />
                <DownloadApp />
            </>
        );
    };

    const BannerList = () => {
        return (
            <>
                <InputSearch
                    type="question" // type Search
                    keyword={keyword}
                    onSearch={handleSearch}
                    className={layout.search}
                    buttonStyle={layout.button}
                    placeholder="Tìm kiếm câu hỏi..."
                />

                <PopularCard {...{ listsTags, tags }} className={layout.popular_card} />

                <DownloadApp />
            </>
        );
    };

    const handleClickFilter = () => {
        toggleFilter();
        // event tracking
        trackingEvent('nhấp vào icon "Bộ lọc" để lọc câu hỏi khi xem bằng mobile', 'Mục phổ biến');
    };

    const renderDownloadApp = () => {
        return <DownloadApp />;
    };

    const renderBannerRight = () => {
        if (actionPage !== 'lists') {
            return BannerDetail();
        }

        return BannerList();
    };

    const filterClassName = classNames([layout.filter, { [layout.open]: openFilter }]);

    const wrapperClassName = classNames([layout.wrapper, { [layout.mobile]: isMobile }]);

    return (
        <SearchLayout
            type="question" // type Search
            keyword={keyword}
            onSearch={handleSearch}
            onClickFilter={handleClickFilter}
            showFilter={actionPage === 'lists'}
        >
            <div className={wrapperClassName}>
                <ViewMedia is={['mobile', 'tablet']}>
                    <div className={filterClassName}>
                        <PopularCard onclose={toggleFilter} disableTitle={isMobile} />
                    </div>
                </ViewMedia>
                <LayoutContainer>
                    <div ref={divRef} className={layout.breadcrumb}>
                        <Breadcrumb paths={actionPage === 'lists' ? paths : pathDetails} />
                    </div>
                    <ViewMedia is={['mobile', 'tablet']}>
                        {actionPage === 'lists' && <ListFilter {...{ listsTags }} className={layout.list_filter} />}
                    </ViewMedia>
                </LayoutContainer>
                <LayoutResult
                    actionPage={actionPage}
                    hasFormAdvisory={false}
                    {...{ renderDownloadApp }}
                    extendComponent={renderBannerRight()}
                    hasExtendComponent={!isMobile}
                >
                    {children}

                    {/* GA */}
                    <PageView />
                </LayoutResult>
            </div>
        </SearchLayout>
    );
};

export default Layout;
